// src/pages/pmc/PmcContractorDocumentsPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast from "react-hot-toast";

const clean = (v = "") => String(v || "").trim().toLowerCase();

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    x.city ||
    ""
  );
}

function isStrictNafedProject(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeToProject(project, nafedScope) {
  if (!nafedScope?.onlyNafedApply && !nafedScope?.strictNafedApplyOnly) {
    return true;
  }

  const scopeState = clean(nafedScope?.state);
  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const stateOk = !scopeState || clean(getState(project)) === scopeState;
  const districtOk =
    !allowedDistricts.length ||
    allowedDistricts.includes(clean(getDistrict(project)));

  return isStrictNafedProject(project) && stateOk && districtOk;
}

function documentMatchesAssignedProject(docRow, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    docRow.projectId,
    docRow.tenderId,
    docRow.workflowProjectId,
    docRow.wfProjectId,
    docRow.godownProjectId,
    docRow.godownApplicationDocId,
    docRow.applicationDocId,
    docRow.linkedProjectId,
    docRow.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    docRow.applicationId,
    docRow.godownApplicationId,
    docRow.linkedApplicationId,
    docRow.pacsApplicationId,
    docRow.projectApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function Badge({ status }) {
  const s = String(status || "").toUpperCase();

  let type = "pending";
  if (s.includes("APPROVED")) type = "approved";
  if (s.includes("REJECT")) type = "rejected";

  return <span className={`doc-badge ${type}`}>{status || "PENDING_VERIFICATION"}</span>;
}

function StatCard({ label, value, note }) {
  return (
    <div className="doc-stat-card">
      <label>{label}</label>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

export default function PmcContractorDocumentsPane({ nafedScope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [remarksMap, setRemarksMap] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  const getAssignedProjects = async () => {
    const snap = await getDocs(collection(db, "godown_projects"));

    const assigned = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => applyNafedScopeToProject(p, nafedScope));

    const projectIds = new Set(assigned.map((p) => String(p.id)));

    const applicationIds = new Set(
      assigned.map((p) => String(p.applicationId || "")).filter(Boolean)
    );

    return { assigned, projectIds, applicationIds };
  };

  const loadRows = async () => {
    try {
      setLoading(true);

      const docsSnap = await getDocs(collection(db, "contractor_documents"));
      const godownSnap = await getDocs(collection(db, "godown_projects"));

      const godownList = godownSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      let assignedProjectIds = new Set();
      let assignedApplicationIds = new Set();

      if (isNafedMode) {
        const scope = await getAssignedProjects();
        assignedProjectIds = scope.projectIds;
        assignedApplicationIds = scope.applicationIds;
      }

      let list = docsSnap.docs.map((d) => {
        const x = { id: d.id, ...d.data() };

        const linkedGodown =
          godownList.find(
            (g) =>
              g.id === x.projectId ||
              g.id === x.godownProjectId ||
              g.id === x.tenderId ||
              g.id === x.workflowProjectId ||
              g.applicationId === x.applicationId ||
              g.applicationId === x.godownApplicationId ||
              g.applicationId === x.projectApplicationId
          ) ||
          godownList.find(
            (g) =>
              clean(g.pacsName) ===
              clean(x.pacsName || x.ownerCompany || x.organization || "")
          );

        return {
          ...x,
          linkedGodownId: linkedGodown?.id || "",
          nafedProject: x.nafedProject === true || linkedGodown?.nafedProject === true,
          projectSource: x.projectSource || linkedGodown?.projectSource || "",
          applicationPortal:
            x.applicationPortal || linkedGodown?.applicationPortal || "",
          state: x.state || linkedGodown?.state || "",
          district: x.district || linkedGodown?.district || "—",
          projectName: x.projectName || x.title || linkedGodown?.projectName || "Project",
          pacsName: x.pacsName || linkedGodown?.pacsName || "—",
          contractorName: x.contractorName || x.vendorName || "—",
          vendorUid: x.vendorUid || x.contractorUid || "—",
          docType: x.docType || "Document",
          fileName: x.fileName || "—",
          fileUrl: x.fileUrl || "",
          remarks: x.remarks || "",
          pmcRemarks: x.pmcRemarks || "",
          pmcStatus: x.pmcStatus || "PENDING_VERIFICATION",
        };
      });

      if (isNafedMode) {
        const scopeState = clean(nafedScope?.state);
        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        list = list.filter((x) => {
          const linked = documentMatchesAssignedProject(
            x,
            assignedProjectIds,
            assignedApplicationIds
          );

          const strictDoc = x.nafedProject === true && x.projectSource === "NAFED";
          const stateOk = !scopeState || clean(getState(x)) === scopeState;
          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(x)));

          return linked || (strictDoc && stateOk && districtOk);
        });
      }

      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("Contractor documents load failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [JSON.stringify(nafedScope || {})]);

  const summary = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter(
      (x) => String(x.pmcStatus || "").toUpperCase() === "PENDING_VERIFICATION"
    ).length;
    const approved = rows.filter(
      (x) => String(x.pmcStatus || "").toUpperCase() === "APPROVED"
    ).length;
    const rejected = rows.filter(
      (x) => String(x.pmcStatus || "").toUpperCase() === "REJECTED"
    ).length;

    return { total, pending, approved, rejected };
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = [...rows];

    if (statusFilter === "pending") {
      list = list.filter(
        (x) => String(x.pmcStatus || "").toUpperCase() === "PENDING_VERIFICATION"
      );
    }

    if (statusFilter === "approved") {
      list = list.filter(
        (x) => String(x.pmcStatus || "").toUpperCase() === "APPROVED"
      );
    }

    if (statusFilter === "rejected") {
      list = list.filter(
        (x) => String(x.pmcStatus || "").toUpperCase() === "REJECTED"
      );
    }

    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((x) =>
        [
          x.projectName,
          x.pacsName,
          x.district,
          x.state,
          x.contractorName,
          x.vendorUid,
          x.docType,
          x.fileName,
          x.id,
          x.tenderId,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    return list;
  }, [rows, search, statusFilter]);

  const setDecision = async (row, decision) => {
    try {
      setSavingId(row.id);

      const pmcRemarks = remarksMap[row.id] ?? row.pmcRemarks ?? "";

      await updateDoc(doc(db, "contractor_documents", row.id), {
        pmcStatus: decision,
        pmcRemarks,
        pmcDecisionAt: serverTimestamp(),
      });

      toast.success(`Document ${decision.toLowerCase()} successfully.`);
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("Document status update failed.");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="doc-page">
      <style>{`
        .doc-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .doc-hero {
          background: linear-gradient(135deg, #062b55, #064e3b);
          color: white;
          border-radius: 22px;
          padding: 24px;
          box-shadow: 0 18px 40px rgba(6,43,85,.20);
        }

        .doc-hero small {
          color: #bbf7d0;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .18em;
        }

        .doc-hero h2 {
          margin: 6px 0 0;
          font-size: 26px;
          font-weight: 950;
        }

        .doc-hero p {
          margin: 8px 0 0;
          color: #d1fae5;
          font-size: 13px;
          line-height: 1.7;
          max-width: 900px;
        }

        .doc-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .doc-stat-card {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .doc-stat-card label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .doc-stat-card b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .doc-stat-card small {
          display: block;
          margin-top: 5px;
          color: #64748b;
          font-size: 11px;
          font-weight: 750;
        }

        .doc-filter {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          padding: 16px;
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 12px;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .doc-field {
          display: grid;
          gap: 6px;
        }

        .doc-field label {
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .doc-input {
          height: 40px;
          border: 1px solid #cbd5e1;
          border-radius: 11px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          background: white;
          color: #071f45;
          outline: none;
        }

        .doc-table-card {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .doc-table-head {
          padding: 15px 18px;
          background: #f8fafc;
          border-bottom: 1.5px solid #111827;
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        }

        .doc-table-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .doc-table-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }

        .doc-table-wrap {
          overflow-x: auto;
        }

        .doc-table {
          width: 100%;
          min-width: 1350px;
          border-collapse: collapse;
          font-size: 12px;
        }

        .doc-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 12px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .doc-table td {
          padding: 12px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
        }

        .doc-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .doc-table tbody tr:hover td {
          background: #eef6ff;
        }

        .doc-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .doc-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .doc-badge.pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .doc-badge.approved {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .doc-badge.rejected {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .doc-btn {
          border: 0;
          border-radius: 10px;
          padding: 8px 11px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
          text-decoration: none;
          text-align: center;
          display: inline-block;
        }

        .doc-btn.primary {
          background: #062b55;
          color: white;
        }

        .doc-btn.green {
          background: #16a34a;
          color: white;
        }

        .doc-btn.red {
          background: #ef4444;
          color: white;
        }

        .doc-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .doc-row-actions {
          display: grid;
          gap: 7px;
        }

        .remark-input {
          width: 100%;
          min-height: 68px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 8px;
          font-size: 12px;
          font-weight: 750;
          outline: none;
          resize: vertical;
        }

        .empty {
          padding: 35px;
          text-align: center;
          color: #64748b;
          font-weight: 850;
        }

        @media(max-width:1000px) {
          .doc-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .doc-filter {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width:700px) {
          .doc-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="doc-hero">
        <small>Contractor Documents Control Room</small>
        <h2>Project-wise Document Verification</h2>
        <p>
          View contractor uploaded documents, verify files, approve/reject and
          add PMC remarks. NAFED mode shows only allocated state/district scoped
          projects.
        </p>
      </section>

      <section className="doc-stats">
        <StatCard label="Total Documents" value={summary.total} note="Scoped uploaded files" />
        <StatCard label="Pending" value={summary.pending} note="Awaiting verification" />
        <StatCard label="Approved" value={summary.approved} note="Verified documents" />
        <StatCard label="Rejected" value={summary.rejected} note="Correction required" />
      </section>

      <section className="doc-filter">
        <div className="doc-field">
          <label>Status Filter</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="doc-input"
          >
            <option value="all">All Documents</option>
            <option value="pending">Pending Verification</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="doc-field">
          <label>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search project, PACS, contractor, document type..."
            className="doc-input"
          />
        </div>
      </section>

      <section className="doc-table-card">
        <div className="doc-table-head">
          <div>
            <h3>Contractor Document Register</h3>
            <p>Professional black-line table with PMC approval action.</p>
          </div>
          <div className="muted">
            Records: <b>{filteredRows.length}</b>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading contractor documents...</div>
        ) : filteredRows.length === 0 ? (
          <div className="empty">No contractor document found.</div>
        ) : (
          <div className="doc-table-wrap">
            <table className="doc-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Project</th>
                  <th>Contractor</th>
                  <th>Document Type</th>
                  <th>File Name</th>
                  <th>Contractor Remarks</th>
                  <th>File</th>
                  <th>PMC Status</th>
                  <th>PMC Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((r, idx) => (
                  <tr key={r.id}>
                    <td style={{ textAlign: "center", fontWeight: 950 }}>
                      {idx + 1}
                    </td>

                    <td>
                      <div className="doc-title">{r.projectName}</div>
                      <div className="muted">
                        {r.pacsName} • {r.district || "—"}
                      </div>
                      <div className="muted">State: {r.state || "—"}</div>
                      <div className="muted">
                        Tender ID: {r.tenderId || r.projectId || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="doc-title">{r.contractorName}</div>
                      <div className="muted">UID: {r.vendorUid}</div>
                    </td>

                    <td>{r.docType}</td>

                    <td>{r.fileName}</td>

                    <td>{r.remarks || "—"}</td>

                    <td style={{ textAlign: "center" }}>
                      {r.fileUrl ? (
                        <a
                          href={r.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="doc-btn primary"
                        >
                          View
                        </a>
                      ) : (
                        <span style={{ color: "#dc2626", fontWeight: 950 }}>
                          Missing
                        </span>
                      )}
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <Badge status={r.pmcStatus} />
                    </td>

                    <td>
                      <textarea
                        value={remarksMap[r.id] ?? r.pmcRemarks ?? ""}
                        onChange={(e) =>
                          setRemarksMap((p) => ({
                            ...p,
                            [r.id]: e.target.value,
                          }))
                        }
                        placeholder="PMC remarks..."
                        className="remark-input"
                      />
                    </td>

                    <td>
                      <div className="doc-row-actions">
                        <button
                          type="button"
                          disabled={savingId === r.id}
                          onClick={() => setDecision(r, "APPROVED")}
                          className="doc-btn green"
                        >
                          Approve
                        </button>

                        <button
                          type="button"
                          disabled={savingId === r.id}
                          onClick={() => setDecision(r, "REJECTED")}
                          className="doc-btn red"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}